import { AxiosInstance } from "axios";
import { createParser } from "eventsource-parser";
import { useMemo, useRef, useState } from "react";

import type {
  ChatWorkflowRequestBodyDto,
  UiStreamAction,
} from "@intelligems/ig-types";
import { isAbortError, processStreamResponse } from "@intelligems/ig-utils";

const chatWorkflow = async (
  api: AxiosInstance,
  payload: ChatWorkflowRequestBodyDto,
  signal?: AbortSignal
) => {
  const resp = await api.post(`/workflows/execution/chat`, payload, {
    headers: {
      Accept: "text/event-stream",
    },
    responseType: "stream",
    adapter: "fetch",
    signal,
  });

  return resp;
};

export const useChatWorkflowShared = (
  api: AxiosInstance,
  callback: (message: UiStreamAction) => void
) => {
  const [isPending, setIsPending] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const parser = useMemo(() => {
    return createParser({
      onEvent: (event) => {
        let parsed: { message: UiStreamAction } | null = null;
        try {
          parsed = JSON.parse(event.data) as { message: UiStreamAction };
        } catch (e) {
          console.log("Non-JSON data:", e, event.data);
        }

        if (parsed) {
          callback(parsed.message);
        }
      },
    });
  }, [callback]);

  const mutate = async (data: ChatWorkflowRequestBodyDto) => {
    setIsPending(true);
    // Defensive: abort any in-flight stream before taking over
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      const resp = await chatWorkflow(api, data, controller.signal);
      await processStreamResponse(resp, parser);
    } catch (err) {
      if (isAbortError(err)) {
        return;
      }
      throw err;
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
      setIsPending(false);
    }
  };

  const stop = () => {
    abortControllerRef.current?.abort();
  };

  return { mutate, isPending, stop };
};
